/**
 * "Created" date-range filter for admin clients.
 *
 * Bounds are local calendar dates (`YYYY-MM-DD`, as produced by a date input).
 * The range is inclusive: `from` starts at local midnight and `through` ends
 * just before the *next local calendar day's* midnight (not `+ 86400000`, which
 * is wrong on 23h/25h DST days). An empty bound is open.
 */
export interface AdminClientCreatedRange {
  /** Inclusive lower bound, `YYYY-MM-DD`; empty string means unbounded. */
  from: string;
  /** Inclusive upper bound, `YYYY-MM-DD`; empty string means unbounded. */
  through: string;
}

export type AdminClientCreatedRangeError =
  "invalid-from" | "invalid-through" | "from-after-through";

export const EMPTY_ADMIN_CLIENT_CREATED_RANGE: AdminClientCreatedRange = {
  from: "",
  through: "",
};

interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIMESTAMP =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(?:Z|[+-](\d{2}):(\d{2}))$/;

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function isValidCalendarDate({ year, month, day }: CalendarDate): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const days = [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return day <= days[month - 1];
}

function parseDateOnly(value: string): CalendarDate | null {
  const match = DATE_ONLY.exec(value.trim());
  if (!match) return null;
  const date = { year: +match[1], month: +match[2], day: +match[3] };
  return isValidCalendarDate(date) ? date : null;
}

/** Epoch ms of local midnight for the given calendar day (day may overflow). */
function localMidnight({ year, month, day }: CalendarDate): number {
  const date = new Date(2000, 0, 1);
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function compareDates(a: CalendarDate, b: CalendarDate): number {
  return a.year - b.year || a.month - b.month || a.day - b.day;
}

/** Validates the bounds; returns the first problem found or `null`. */
export function validateAdminClientCreatedRange(
  range: AdminClientCreatedRange
): AdminClientCreatedRangeError | null {
  const from = range.from.trim() ? parseDateOnly(range.from) : undefined;
  const through = range.through.trim()
    ? parseDateOnly(range.through)
    : undefined;
  if (from === null) return "invalid-from";
  if (through === null) return "invalid-through";
  if (from && through && compareDates(from, through) > 0) {
    return "from-after-through";
  }
  return null;
}

/** True when the range is valid and has at least one bound set. */
export function isAdminClientCreatedRangeActive(
  range: AdminClientCreatedRange
): boolean {
  return (
    (range.from.trim() !== "" || range.through.trim() !== "") &&
    validateAdminClientCreatedRange(range) === null
  );
}

/**
 * Whether `createdAt` falls inside the range. An inactive range (no bounds, or
 * invalid bounds — surface those via `validateAdminClientCreatedRange`) matches
 * everything. With an active range, a missing or malformed `createdAt` never
 * matches. Accepts a date-only `YYYY-MM-DD` (compared as a local calendar date,
 * no UTC shift) or an ISO timestamp with `Z` or a `±HH:MM` offset.
 */
export function matchesAdminClientCreatedRange(
  createdAt: string | null | undefined,
  range: AdminClientCreatedRange
): boolean {
  if (!isAdminClientCreatedRangeActive(range)) return true;
  if (!createdAt) return false;

  const from = range.from.trim() ? parseDateOnly(range.from) : null;
  const through = range.through.trim() ? parseDateOnly(range.through) : null;
  const value = createdAt.trim();

  const dateOnly = parseDateOnly(value);
  if (dateOnly) {
    return (
      (!from || compareDates(dateOnly, from) >= 0) &&
      (!through || compareDates(dateOnly, through) <= 0)
    );
  }
  if (DATE_ONLY.test(value)) return false;

  const match = TIMESTAMP.exec(value);
  if (!match) return false;
  const [, y, mo, d, h, mi, s = "0", oh = "0", om = "0"] = match;
  if (
    !isValidCalendarDate({ year: +y, month: +mo, day: +d }) ||
    +h > 23 ||
    +mi > 59 ||
    +s > 59 ||
    +oh > 23 ||
    +om > 59
  ) {
    return false;
  }
  const time = Date.parse(value);
  if (Number.isNaN(time)) return false;

  return (
    (!from || time >= localMidnight(from)) &&
    (!through || time < localMidnight({ ...through, day: through.day + 1 }))
  );
}
